import type { ManualSection } from '../manual.types';

export const usuariosSection: ManualSection = {
  slug: 'usuarios',
  title: 'Usuarios, roles y sesión',
  icon: 'ki-profile-user',
  summary:
    'Qué puede hacer cada rol, las reglas de jerarquía, cómo funciona la sesión y cómo recuperar una contraseña.',
  audience: 'Administradores y QA.',
  topics: [
    {
      id: 'matriz-de-roles',
      title: 'Los cuatro roles',
      blocks: [
        {
          kind: 'table',
          head: ['Recurso', 'operador', 'supervisor', 'admin', 'superadmin'],
          align: ['start', 'center', 'center', 'center', 'center'],
          rows: [
            ['Dashboard, Reportes y todas las lecturas', '✓', '✓', '✓', '✓'],
            ['Escribir en CPD y Recargar FS', '', '✓', '✓', '✓'],
            ['Escribir en Salientes / Entrantes / Dialplan', '', '✓', '✓', '✓'],
            ['Recargar Entrantes y Dialplan', '', '✓', '✓', '✓'],
            ['Recargar Salientes', '', '', '✓', '✓'],
            ['Usuarios', '', '', '✓', '✓'],
            ['Auditoría', '', '', '✓', '✓']
          ]
        },
        {
          kind: 'p',
          text: 'El rol **operador** ve toda la plataforma en solo lectura. Conserva "Ver política efectiva" en CPD porque es una consulta, no un cambio. El rol **superadmin** puede todo.'
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Entrar a una pantalla que tu rol no permite',
          text: 'Te manda a la página de "sin acceso". Además el menú lateral esconde los módulos que no puedes ver, así que normalmente ni llegas.'
        }
      ]
    },
    {
      id: 'jerarquia',
      title: 'Reglas de jerarquía (por qué a veces no te deja)',
      blocks: [
        {
          kind: 'p',
          text: 'Además de los roles, la administración de usuarios tiene reglas propias. Estas las valida siempre el backend, aunque la pantalla también las refleje.'
        },
        {
          kind: 'ul',
          items: [
            'Solo un **superadmin** puede asignar o quitar el rol superadmin.',
            'Solo un **superadmin** puede **editar, eliminar o resetear la contraseña** de alguien que ya es superadmin. A un admin la pantalla le deshabilita esos botones y le explica el motivo al pasar el cursor.',
            'Nadie puede **eliminarse ni deshabilitarse a sí mismo**. Es protección contra quedarse sin acceso.',
            'Un usuario siempre debe quedar con **al menos un rol**, y con al menos un correo o un nombre de usuario.',
            'Al cambiar contraseña, roles o el estado habilitado de alguien, **se cierran todas sus sesiones** y tiene que volver a entrar.'
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Si te sale un error al eliminar',
          text: 'El mensaje te dice el motivo exacto (por ejemplo, "Solo un superadmin puede borrar otro superadmin"). No es una falla de la aplicación: es la regla haciendo su trabajo.'
        }
      ]
    },
    {
      id: 'sesion',
      title: 'Cómo funciona la sesión',
      blocks: [
        {
          kind: 'ul',
          items: [
            'El token de acceso dura **15 minutos**, pero **no te saca**: la aplicación lo renueva sola un minuto antes de que venza, sin que lo notes.',
            'La sesión completa dura hasta **8 horas**.',
            'Solo se te avisa después de **30 minutos sin actividad**. Navegar entre pantallas no reinicia ese contador; interactuar sí.',
            'Si una petición falla por token vencido, la aplicación lo renueva y **reintenta esa misma petición una vez**, de forma transparente.'
          ]
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'Si te expulsa al iniciar sesión en otra parte',
          text: 'Por seguridad, reutilizar una sesión ya renovada invalida toda la familia de sesiones. Entrar desde dos navegadores a la vez con la misma cuenta puede tirar una de las dos. Para pruebas simultáneas usa cuentas distintas.'
        }
      ]
    },
    {
      id: 'recuperar-contrasena',
      title: 'Recuperar la contraseña',
      blocks: [
        {
          kind: 'steps',
          items: [
            'En el login, presiona **¿Olvidaste tu contraseña?**',
            'Escribe tu usuario o tu correo. La pantalla **siempre** responde lo mismo, exista o no la cuenta — es a propósito, para que nadie pueda averiguar qué usuarios existen.',
            'Si la cuenta existe y tiene correo dado de alta, llega un enlace.',
            'El enlace abre la pantalla de contraseña nueva. Mínimo 8 caracteres, con confirmación.',
            'Al guardar se cierran **todas** tus sesiones y tienes que entrar con la nueva.'
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'El enlace es de un solo uso y expira',
          text: 'Si ya lo usaste o pasó demasiado tiempo, verás "token inválido o expirado". Pide uno nuevo desde la misma pantalla.'
        },
        {
          kind: 'p',
          text: 'Un administrador también puede resetear la contraseña de alguien desde la pantalla de Usuarios; en ese caso el sistema genera una contraseña temporal que se le entrega al usuario.'
        }
      ]
    }
  ]
};
